import { PageHeading } from "@/components/ui/page";
import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, Package, X } from "lucide-react";
import { PermissionGate } from "@/features/auth/components/PermissionGate";
import { useToast } from "@/hooks/use-toast";
import { getApiErrorMessage, stockTransfersAPI } from "@/lib/api";
import { formatDate, formatNumber } from "@/lib/utils";
import { Loading } from "@/components/ui/loader";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { confirmAction, promptAction } from "@/components/ui/confirm-dialog";

const STATUS_MAP: Record<string, string> = {
	draft: "stockTransfers.status.draft",
	pending: "stockTransfers.status.pending",
	approved: "stockTransfers.status.approved",
	in_transit: "stockTransfers.status.inTransit",
	received: "stockTransfers.status.received",
	rejected: "stockTransfers.status.rejected",
	cancelled: "stockTransfers.status.cancelled",
};

const StockTransferDetailPage: React.FC = () => {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const { t } = useTranslation();
	const { toast } = useToast();
	const [transfer, setTransfer] = useState<any>(null);
	const [loading, setLoading] = useState(true);
	const [receiveDialogOpen, setReceiveDialogOpen] = useState(false);
	const [receiveQuantities, setReceiveQuantities] = useState<Record<number, number>>({});
	const [damagedQuantities, setDamagedQuantities] = useState<Record<number, number>>({});
	const [submitting, setSubmitting] = useState(false);

	useEffect(() => {
		if (id) fetchOne();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [id]);

	const fetchOne = async () => {
		if (!id) return;
		try {
			setLoading(true);
			const res = await stockTransfersAPI.getById(id);
			setTransfer(res);
			const initial: Record<number, number> = {};
			(res?.items ?? []).forEach((i: any) => {
				initial[i.id] = Number(i.quantity) - Number(i.receivedQuantity || 0);
			});
			setReceiveQuantities(initial);
		} catch (err) {
			toast({
				title: t("common.error"),
				description: t("stockTransfers.errors.fetchFailed"),
				variant: "destructive",
			});
		} finally {
			setLoading(false);
		}
	};

	const handleApprove = async () => {
		if (!id || !transfer) return;
		if (
			!(await confirmAction(t("stockTransfers.confirmApprove"), {
				confirmLabel: t("stockTransfers.approve"),
				destructive: false,
			}))
		)
			return;
		try {
			setSubmitting(true);
			await stockTransfersAPI.approve(parseInt(id, 10));
			toast({ title: t("common.success"), description: t("stockTransfers.messages.approved") });
			fetchOne();
		} catch (err: any) {
			toast({
				title: t("common.error"),
				description: getApiErrorMessage(err, t, t("stockTransfers.errors.approveFailed")),
				variant: "destructive",
			});
		} finally {
			setSubmitting(false);
		}
	};

	const handleReceive = async () => {
		if (!id || !transfer) return;
		const items = (transfer.items ?? [])
			.map((i: any) => ({
				stockTransferItemId: i.id,
				receivedQuantity: receiveQuantities[i.id] ?? 0,
				damagedQuantity: damagedQuantities[i.id] ?? 0,
			}))
			.filter((x: any) => x.receivedQuantity > 0 || x.damagedQuantity > 0);
		if (items.length === 0) {
			toast({
				title: t("common.error"),
				description: t("stockTransfers.errors.enterQuantities"),
				variant: "destructive",
			});
			return;
		}
		try {
			setSubmitting(true);
			await stockTransfersAPI.receive(parseInt(id, 10), { items });
			toast({ title: t("common.success"), description: t("stockTransfers.messages.received") });
			setReceiveDialogOpen(false);
			fetchOne();
		} catch (err: any) {
			toast({
				title: t("common.error"),
				description: getApiErrorMessage(err, t, t("stockTransfers.errors.receiveFailed")),
				variant: "destructive",
			});
		} finally {
			setSubmitting(false);
		}
	};

	const runAction = async (fn: () => Promise<unknown>, successKey: string, errorKey: string) => {
		if (!id) return;
		try {
			setSubmitting(true);
			await fn();
			toast({ title: t("common.success"), description: t(successKey) });
			fetchOne();
		} catch (err: any) {
			toast({
				title: t("common.error"),
				description: getApiErrorMessage(err, t, t(errorKey)),
				variant: "destructive",
			});
		} finally {
			setSubmitting(false);
		}
	};

	const handleSubmit = () =>
		runAction(
			() => stockTransfersAPI.submit(parseInt(id!, 10)),
			"stockTransfers.messages.submitted",
			"stockTransfers.errors.submitFailed",
		);

	const handleReject = async () => {
		const reason = await promptAction(t("stockTransfers.rejectReasonPrompt"), {
			confirmLabel: t("stockTransfers.reject"),
		});
		if (reason === null) return;
		return runAction(
			() => stockTransfersAPI.reject(parseInt(id!, 10), reason),
			"stockTransfers.messages.rejected",
			"stockTransfers.errors.rejectFailed",
		);
	};

	const handleCancel = async () => {
		if (!id || !(await confirmAction(t("stockTransfers.confirmCancel")))) return;
		try {
			setSubmitting(true);
			await stockTransfersAPI.cancel(parseInt(id, 10));
			toast({ title: t("common.success"), description: t("stockTransfers.messages.cancelled") });
			fetchOne();
		} catch (err: any) {
			toast({
				title: t("common.error"),
				description: getApiErrorMessage(err, t, t("stockTransfers.errors.cancelFailed")),
				variant: "destructive",
			});
		} finally {
			setSubmitting(false);
		}
	};

	if (loading) return <Loading />;
	if (!transfer) {
		return (
			<div className="flex flex-col items-center justify-center gap-4 p-10 text-center">
				<p className="text-muted-foreground">{t("stockTransfers.errors.fetchFailed")}</p>
				<div className="flex gap-2">
					<Button variant="outline" onClick={() => navigate(-1)}>
						{t("common.back")}
					</Button>
					<Button onClick={() => fetchOne()}>{t("common.tryAgain")}</Button>
				</div>
			</div>
		);
	}

	const canSubmit = transfer.status === "draft";
	const canApprove = transfer.status === "pending";
	const canReject = transfer.status === "pending";
	const canReceive = transfer.status === "approved" || transfer.status === "in_transit";
	const canCancel = transfer.status === "draft" || transfer.status === "pending" || transfer.status === "approved";

	return (
		<div className="p-4 md:p-6 space-y-4">
			<div className="flex items-center gap-4">
				<div>
					<PageHeading className="">
						{t("stockTransfers.detailTitle")} #{transfer.id}
					</PageHeading>
					<p className="text-muted-foreground text-sm">
						{transfer.fromBranch?.name} <span className="rtl:rotate-180">→</span> {transfer.toBranch?.name}
					</p>
				</div>
			</div>

			<Card>
				<CardHeader>
					<CardTitle className="text-base gap-3 flex items-center">
						<span className="">{t("stockTransfers.info")}</span>
						<Badge
							variant={
								transfer.status === "received"
									? "success"
									: transfer.status === "cancelled"
										? "destructive"
										: "default"
							}
						>
							{t(STATUS_MAP[transfer.status] ?? transfer.status)}
						</Badge>
					</CardTitle>
				</CardHeader>
				<CardContent className="py-4">
					<div className="grid grid-cols-1 md:grid-cols-2 gap-y-2 gap-x-5">
						<div className="flex items-center bg-muted p-2 rounded-lg">
							<span className="font-medium text-muted-foreground w-36 block">
								{t("stockTransfers.fromBranch")}:
							</span>
							<span className="truncate">{transfer.fromBranch?.name ?? "-"}</span>
						</div>
						<div className="flex items-center bg-muted p-2 rounded-lg">
							<span className="font-medium text-muted-foreground w-36 block">
								{t("stockTransfers.toBranch")}:
							</span>
							<span className="truncate">{transfer.toBranch?.name ?? "-"}</span>
						</div>
						<div className="flex items-center bg-muted p-2 rounded-lg">
							<span className="font-medium text-muted-foreground w-36 block">
								{t("stockTransfers.requestedAt")}:
							</span>
							<span>{formatDate(transfer.requestedAt)}</span>
						</div>
						{transfer.requestedBy && (
							<div className="flex items-center bg-muted p-2 rounded-lg">
								<span className="font-medium text-muted-foreground w-36 block">
									{t("stockTransfers.requestedBy")}:
								</span>
								<span>
									{transfer.requestedBy.firstName} {transfer.requestedBy.lastName}
								</span>
							</div>
						)}
						{transfer.approvedAt && (
							<div className="flex items-center bg-muted p-2 rounded-lg">
								<span className="font-medium text-muted-foreground w-36 block">
									{t("stockTransfers.approvedAt")}:
								</span>
								<span>{formatDate(transfer.approvedAt)}</span>
							</div>
						)}
						{transfer.receivedAt && (
							<div className="flex items-center bg-muted p-2 rounded-lg">
								<span className="font-medium text-muted-foreground w-36 block">
									{t("stockTransfers.receivedAt")}:
								</span>
								<span>{formatDate(transfer.receivedAt)}</span>
							</div>
						)}
						{transfer.notes && (
							<div className="flex items-center col-span-1 md:col-span-2 bg-muted p-2 rounded-lg">
								<span className="font-medium text-muted-foreground w-36 block">
									{t("common.notes")}:
								</span>
								<span className="break-words">{transfer.notes}</span>
							</div>
						)}
					</div>
				</CardContent>
			</Card>

			<Card>
				<CardHeader>
					<CardTitle className="text-base flex items-center gap-2">
						<Package className="h-4 w-4" />
						{t("stockTransfers.items")}
					</CardTitle>
				</CardHeader>
				<CardContent className="p-2 pt-0 pb-3">
					<div className="rounded-xl border my-0">
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead className="text-center">{t("stockTransfers.product")}</TableHead>
									<TableHead className="text-center">{t("stockTransfers.quantity")}</TableHead>
									<TableHead className="text-center">
										{t("stockTransfers.receivedQuantity")}
									</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{(transfer.items ?? []).map((item: any) => (
									<TableRow key={item.id}>
										<TableCell className="text-center">
											{item.product?.name ?? item.productId}
										</TableCell>
										<TableCell className="text-center">{formatNumber(item.quantity)}</TableCell>
										<TableCell className="text-center">
											{formatNumber(item.receivedQuantity ?? 0)}
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</div>
				</CardContent>
			</Card>

			<div className="flex flex-wrap justify-end gap-4">
				<PermissionGate permission="stockTransfers.update">
					{canSubmit && (
						<Button onClick={handleSubmit} disabled={submitting} className="gap-2 px-8">
							<Check className="h-4 w-4" />
							{t("stockTransfers.submit")}
						</Button>
					)}
				</PermissionGate>
				<PermissionGate permission="stockTransfers.approve">
					{canApprove && (
						<Button onClick={handleApprove} disabled={submitting} className="gap-2 px-8">
							<Check className="h-4 w-4" />
							{t("stockTransfers.approve")}
						</Button>
					)}
				</PermissionGate>
				<PermissionGate permission="stockTransfers.approve">
					{canReject && (
						<Button variant="outline" onClick={handleReject} disabled={submitting} className="gap-2 px-8">
							<X className="h-4 w-4" />
							{t("stockTransfers.reject")}
						</Button>
					)}
				</PermissionGate>
				<PermissionGate permission="stockTransfers.receive">
					{canReceive && (
						<Button
							variant="secondary"
							onClick={() => setReceiveDialogOpen(true)}
							disabled={submitting}
							className="gap-2 px-12"
						>
							<Package className="h-4 w-4" />
							{t("stockTransfers.receive")}
						</Button>
					)}
				</PermissionGate>
				<PermissionGate permission="stockTransfers.update">
					{canCancel && (
						<Button
							variant="destructive"
							onClick={handleCancel}
							disabled={submitting}
							className="gap-2 px-12"
						>
							<X className="h-4 w-4" />
							{t("stockTransfers.cancelTransfer")}
						</Button>
					)}
				</PermissionGate>
			</div>

			<Dialog open={receiveDialogOpen} onOpenChange={setReceiveDialogOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t("stockTransfers.receiveDialogTitle")}</DialogTitle>
						<DialogDescription>{t("stockTransfers.receiveDialogDescription")}</DialogDescription>
					</DialogHeader>
					<div className="space-y-4 py-4">
						{(transfer.items ?? []).map((item: any) => {
							const pending =
								Number(item.quantity) -
								Number(item.receivedQuantity || 0) -
								Number(item.damagedQuantity || 0);
							if (pending <= 0) return null;
							const damaged = damagedQuantities[item.id] ?? 0;
							const good = receiveQuantities[item.id] ?? Math.max(0, pending - damaged);
							return (
								<div key={item.id} className="space-y-1">
									<Label className="text-sm font-medium">
										{item.product?.name ?? item.productId}
									</Label>
									<div className="flex items-center gap-3">
										<div className="flex-1">
											<span className="text-muted-foreground text-xs">
												{t("stockTransfers.received")}
											</span>
											<Input
												aria-label={`${t("stockTransfers.received")}: ${item.product?.name ?? item.productId}`}
												type="number"
												min={0}
												max={pending - damaged}
												step="any"
												value={good}
												onChange={e => {
													const g = Math.max(
														0,
														Math.min(parseFloat(e.target.value) || 0, pending - damaged),
													);
													setReceiveQuantities(prev => ({ ...prev, [item.id]: g }));
												}}
											/>
										</div>
										<div className="flex-1">
											<span className="text-muted-foreground text-xs">
												{t("stockTransfers.damaged")}
											</span>
											<Input
												aria-label={`${t("stockTransfers.damaged")}: ${item.product?.name ?? item.productId}`}
												type="number"
												min={0}
												max={pending}
												step="any"
												value={damaged}
												onChange={e => {
													const d = Math.max(
														0,
														Math.min(parseFloat(e.target.value) || 0, pending),
													);
													// Keep received + damaged within the pending amount.
													setDamagedQuantities(prev => ({ ...prev, [item.id]: d }));
													setReceiveQuantities(prev => ({
														...prev,
														[item.id]: Math.max(0, pending - d),
													}));
												}}
											/>
										</div>
										<span className="text-muted-foreground shrink-0 pt-4 text-sm">
											/ {formatNumber(pending)}
										</span>
									</div>
								</div>
							);
						})}
					</div>
					<DialogFooter>
						<Button variant="outline" onClick={() => setReceiveDialogOpen(false)}>
							{t("common.cancel")}
						</Button>
						<Button onClick={handleReceive} disabled={submitting}>
							{t("stockTransfers.confirmReceive")}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
};

export default StockTransferDetailPage;
